import React, { useEffect, useState } from 'react';
import { getUsers, registerUser, toggleUserActive, toggleMasterAdmin, getClients, deleteUser, deleteUsersBulk, bulkUpdateUsers } from '../lib/api';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { useI18n } from '../contexts/I18nContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Power, Loader2, Shield, ShieldOff, Trash2, CheckSquare, Users, Lock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { PermissionsDialog } from '../components/PermissionsDialog';

export default function UsersPage() {
  const { t } = useI18n();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'ops', client_id: '' });
  const [saving, setSaving] = useState(false);
  const [togglingMaster, setTogglingMaster] = useState(null);
  
  // Selection and bulk actions
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, userId: null, userName: null });
  const [deleting, setDeleting] = useState(false);
  const [bulkEditDialog, setBulkEditDialog] = useState({ open: false });
  const [bulkEditData, setBulkEditData] = useState({ role: '', is_active: '' });
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [permissionsDialog, setPermissionsDialog] = useState({ open: false, user: null });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [usersRes, clientsRes] = await Promise.all([
        getUsers(),
        getClients(),
      ]);
      setUsers(usersRes.data);
      setClients(clientsRes.data);
      setSelectedUsers([]);
    } catch (err) {
      toast.error(t('users.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.password.trim()) {
      toast.error(t('users.requiredFields'));
      return;
    }
    if (form.role === 'client' && !form.client_id) {
      toast.error(t('users.clientRequired'));
      return;
    }

    setSaving(true);
    try {
      await registerUser({
        name: form.name,
        email: form.email,
        password: form.password,
        role: form.role,
        client_id: form.role === 'client' ? form.client_id : null,
      });
      toast.success(t('users.userCreated'));
      setShowForm(false);
      setForm({ name: '', email: '', password: '', role: 'ops', client_id: '' });
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('users.createError'));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (userId) => {
    try {
      await toggleUserActive(userId);
      loadData();
      toast.success(t('users.statusUpdated'));
    } catch (err) {
      toast.error(t('users.statusError'));
    }
  };

  const handleToggleMasterAdmin = async (userId, isMasterAdmin) => {
    setTogglingMaster(userId);
    try {
      const res = await toggleMasterAdmin(userId);
      toast.success(res.data.message);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('users.masterRoleError'));
    } finally {
      setTogglingMaster(null);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteDialog.userId) return;
    setDeleting(true);
    try {
      await deleteUser(deleteDialog.userId);
      toast.success(t('users.userDeleted'));
      setDeleteDialog({ open: false, userId: null, userName: null });
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('users.deleteError'));
    } finally {
      setDeleting(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedUsers.length === 0) return;
    setBulkUpdating(true);
    try {
      const res = await deleteUsersBulk(selectedUsers);
      toast.success(res.data.message);
      setSelectedUsers([]);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('users.deleteError'));
    } finally {
      setBulkUpdating(false);
    }
  };

  const handleBulkUpdate = async () => {
    if (selectedUsers.length === 0) return;
    
    const updateData = {};
    if (bulkEditData.role && bulkEditData.role !== 'none') {
      updateData.role = bulkEditData.role;
    }
    if (bulkEditData.is_active !== '' && bulkEditData.is_active !== 'none') {
      updateData.is_active = bulkEditData.is_active === 'true';
    }
    
    if (Object.keys(updateData).length === 0) {
      toast.error(t('common.selectAtLeastOneField'));
      return;
    }
    
    setBulkUpdating(true);
    try {
      const res = await bulkUpdateUsers(selectedUsers, updateData);
      toast.success(res.data.message);
      setBulkEditDialog({ open: false });
      setBulkEditData({ role: '', is_active: '' });
      setSelectedUsers([]);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('shipments.bulkUpdateError'));
    } finally {
      setBulkUpdating(false);
    }
  };

  const toggleSelectUser = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      // Don't select current user
      setSelectedUsers(users.filter(u => u.id !== currentUser?.id).map(u => u.id));
    }
  };

  const getRoleBadge = (role) => {
    const colors = {
      admin: 'bg-purple-500/20 text-purple-400',
      ops: 'bg-blue-500/20 text-blue-400',
      client: 'bg-emerald-500/20 text-emerald-400',
    };
    const labels = { admin: 'Admin', ops: t('users.operator'), client: t('users.client') };
    return (
      <span className={`text-xs px-2 py-0.5 rounded ${colors[role]}`}>
        {labels[role]}
      </span>
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="users-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl">{t('users.title')}</h1>
            <p className="text-muted-foreground">{users.length} {t('users.title').toLowerCase()}</p>
          </div>
          <div className="flex items-center gap-2">
            {/* Bulk Actions */}
            {selectedUsers.length > 0 && (
              <>
                <Button 
                  variant="outline"
                  onClick={() => setBulkEditDialog({ open: true })}
                  data-testid="bulk-edit-users-btn"
                >
                  <CheckSquare size={16} className="mr-2" />
                  {t('common.edit')} ({selectedUsers.length})
                </Button>
                <Button 
                  variant="destructive"
                  onClick={handleBulkDelete}
                  disabled={bulkUpdating}
                  data-testid="bulk-delete-users-btn"
                >
                  {bulkUpdating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 size={16} className="mr-2" />}
                  {t('common.delete')} ({selectedUsers.length})
                </Button>
              </>
            )}
            
            <Dialog open={showForm} onOpenChange={(open) => { setShowForm(open); if (!open) setForm({ name: '', email: '', password: '', role: 'ops', client_id: '' }); }}>
              <DialogTrigger asChild>
                <Button data-testid="new-user-btn">
                  <Plus size={16} className="mr-2" />
                  {t('users.addUser')}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t('users.addUser')}</DialogTitle>
                  <DialogDescription>
                    {t('users.welcomeEmailNote')}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label>{t('common.name')} *</Label>
                    <Input
                      value={form.name}
                      onChange={(e) => setForm({...form, name: e.target.value})}
                      placeholder={t('common.name')}
                      className="mt-1"
                      data-testid="user-name-input"
                    />
                  </div>
                  <div>
                    <Label>{t('auth.email')} *</Label>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({...form, email: e.target.value})}
                      placeholder="correo@ejemplo.com"
                      className="mt-1"
                      data-testid="user-email-input"
                    />
                  </div>
                  <div>
                    <Label>{t('users.initialPassword')} *</Label>
                    <Input
                      type="password"
                      value={form.password}
                      onChange={(e) => setForm({...form, password: e.target.value})}
                      placeholder="••••••••"
                      className="mt-1"
                      data-testid="user-password-input"
                    />
                  </div>
                  <div>
                    <Label>{t('users.role')} *</Label>
                    <Select value={form.role} onValueChange={(v) => setForm({...form, role: v, client_id: ''})}>
                      <SelectTrigger className="mt-1" data-testid="user-role-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">{t('users.administrator')}</SelectItem>
                        <SelectItem value="ops">{t('users.operator')}</SelectItem>
                        <SelectItem value="client">{t('users.client')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {form.role === 'client' && (
                    <div>
                      <Label>{t('users.associatedClient')} *</Label>
                      <Select value={form.client_id} onValueChange={(v) => setForm({...form, client_id: v})}>
                        <SelectTrigger className="mt-1" data-testid="user-client-select">
                          <SelectValue placeholder={t('users.selectClient')} />
                        </SelectTrigger>
                        <SelectContent>
                          {clients.map(c => (
                            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setShowForm(false)}>{t('common.cancel')}</Button>
                    <Button type="submit" disabled={saving} data-testid="save-user-btn">
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t('users.addUser')}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <div className="bg-card border border-border rounded-sm overflow-hidden">
          <table className="w-full dense-table">
            <thead>
              <tr className="text-left text-xs text-muted-foreground border-b border-border bg-secondary/30">
                <th className="py-2 px-3 w-10">
                  <Checkbox 
                    checked={selectedUsers.length > 0 && selectedUsers.length === users.filter(u => u.id !== currentUser?.id).length}
                    onCheckedChange={toggleSelectAll}
                    data-testid="select-all-users"
                  />
                </th>
                <th className="py-2 px-3 font-medium">Nombre</th>
                <th className="py-2 px-3 font-medium">Email</th>
                <th className="py-2 px-3 font-medium">Rol</th>
                <th className="py-2 px-3 font-medium">Cliente</th>
                <th className="py-2 px-3 font-medium">Estado</th>
                {currentUser?.is_master_admin && (
                  <th className="py-2 px-3 font-medium">Master Admin</th>
                )}
                <th className="py-2 px-3 font-medium w-28">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={currentUser?.is_master_admin ? 8 : 7} className="py-8 text-center text-muted-foreground">{t('common.loading')}</td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan={currentUser?.is_master_admin ? 8 : 7} className="py-8 text-center text-muted-foreground">{t('users.noUsers')}</td></tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id} className={`border-b border-border/50 hover:bg-secondary/30 ${selectedUsers.includes(user.id) ? 'bg-primary/5' : ''}`} data-testid={`user-row-${user.id}`}>
                    <td className="py-2 px-3">
                      {user.id !== currentUser?.id && (
                        <Checkbox 
                          checked={selectedUsers.includes(user.id)}
                          onCheckedChange={() => toggleSelectUser(user.id)}
                          data-testid={`select-user-${user.id}`}
                        />
                      )}
                    </td>
                    <td className="py-2 px-3 font-medium">{user.name}</td>
                    <td className="py-2 px-3 text-sm text-muted-foreground">{user.email}</td>
                    <td className="py-2 px-3">{getRoleBadge(user.role)}</td>
                    <td className="py-2 px-3 text-sm text-muted-foreground">
                      {user.client_id ? clients.find(c => c.id === user.client_id)?.name || '—' : '—'}
                    </td>
                    <td className="py-2 px-3">
                      <span className={`text-xs px-2 py-0.5 rounded ${user.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-500/20 text-zinc-400'}`}>
                        {user.is_active ? t('users.active') : t('users.inactive')}
                      </span>
                    </td>
                    {currentUser?.is_master_admin && (
                      <td className="py-2 px-3">
                        <span className={`text-xs px-2 py-0.5 rounded ${user.is_master_admin ? 'bg-amber-500/20 text-amber-400' : 'bg-zinc-500/20 text-zinc-400'}`}>
                          {user.is_master_admin ? t('common.yes') : t('common.no')}
                        </span>
                      </td>
                    )}
                    <td className="py-2 px-3 flex gap-1">
                      <Button 
                        size="icon" 
                        variant="ghost" 
                        onClick={() => handleToggleActive(user.id)}
                        className={user.is_active ? 'text-amber-400' : 'text-emerald-400'}
                        title={user.is_active ? 'Desactivar usuario' : 'Activar usuario'}
                        data-testid={`toggle-active-${user.id}`}
                      >
                        <Power size={14} />
                      </Button>
                      {currentUser?.is_master_admin && user.role === 'admin' && (
                        <Button 
                          size="icon" 
                          variant="ghost" 
                          onClick={() => handleToggleMasterAdmin(user.id, user.is_master_admin)}
                          className={user.is_master_admin ? 'text-amber-400 hover:text-red-400' : 'text-blue-400 hover:text-amber-400'}
                          title={user.is_master_admin ? 'Quitar rol de Admin Maestro' : 'Promover a Admin Maestro'}
                          disabled={togglingMaster === user.id}
                          data-testid={`toggle-master-admin-${user.id}`}
                        >
                          {togglingMaster === user.id ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : user.is_master_admin ? (
                            <ShieldOff size={14} />
                          ) : (
                            <Shield size={14} />
                          )}
                        </Button>
                      )}
                      {user.id !== currentUser?.id && (
                        <Button 
                          size="icon" 
                          variant="ghost" 
                          onClick={() => setPermissionsDialog({ open: true, user })}
                          className="text-primary hover:text-primary/80"
                          title={currentUser?.role === 'admin' ? 'Configurar permisos' : 'Ver permisos'}
                          data-testid={`permissions-user-${user.id}`}
                        >
                          <Lock size={14} />
                        </Button>
                      )}
                      {user.id !== currentUser?.id && (
                        <Button 
                          size="icon" 
                          variant="ghost" 
                          onClick={() => setDeleteDialog({ open: true, userId: user.id, userName: user.name })}
                          className="text-red-400 hover:text-red-300"
                          title="Eliminar usuario"
                          data-testid={`delete-user-${user.id}`}
                        >
                          <Trash2 size={14} />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialog.open} onOpenChange={(open) => !deleting && setDeleteDialog({ ...deleteDialog, open })}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{t('common.delete')} {t('users.title').split(' ')[0]}</DialogTitle>
              <DialogDescription>
                {t('users.confirmDelete')} <strong>{deleteDialog.userName}</strong>?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialog({ open: false, userId: null, userName: null })} disabled={deleting}>
                {t('common.cancel')}
              </Button>
              <Button variant="destructive" onClick={handleDeleteUser} disabled={deleting} data-testid="confirm-delete-user">
                {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 size={16} className="mr-2" />}
                {t('common.delete')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Edit Dialog */}
        <Dialog open={bulkEditDialog.open} onOpenChange={(open) => !bulkUpdating && setBulkEditDialog({ open })}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{t('users.bulkEdit')}</DialogTitle>
              <DialogDescription>
                {t('users.bulkEditDescription', { count: selectedUsers.length })}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>{t('users.role')}</Label>
                <select
                  className="mt-1 w-full h-10 px-3 py-2 bg-background border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  value={bulkEditData.role}
                  onChange={(e) => setBulkEditData({ ...bulkEditData, role: e.target.value })}
                  data-testid="bulk-role-select"
                >
                  <option value="">{t('users.noChange')}</option>
                  <option value="admin">{t('users.admin')}</option>
                  <option value="ops">{t('users.operator')}</option>
                  <option value="client">{t('users.clientRole')}</option>
                </select>
              </div>
              <div>
                <Label>Estado</Label>
                <select
                  className="mt-1 w-full h-10 px-3 py-2 bg-background border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  value={bulkEditData.is_active}
                  onChange={(e) => setBulkEditData({ ...bulkEditData, is_active: e.target.value })}
                  data-testid="bulk-status-select"
                >
                  <option value="">{t('users.noChange')}</option>
                  <option value="true">{t('users.active')}</option>
                  <option value="false">{t('users.inactive')}</option>
                </select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBulkEditDialog({ open: false })} disabled={bulkUpdating}>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleBulkUpdate} disabled={bulkUpdating} data-testid="confirm-bulk-edit-users">
                {bulkUpdating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckSquare size={16} className="mr-2" />}
                {t('common.update')} ({selectedUsers.length})
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Permissions Dialog */}
        <PermissionsDialog
          open={permissionsDialog.open}
          onOpenChange={(open) => setPermissionsDialog({ open, user: open ? permissionsDialog.user : null })}
          user={permissionsDialog.user}
          readOnly={currentUser?.role !== 'admin'}
        />
      </div>
    </DashboardLayout>
  );
}
